import React from 'react';
import { Dialog } from '@headlessui/react';
import { CpuChipIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

function CostEstimationModal({ costEstimate, onConfirm, onCancel }) {
    const { t } = useTranslation();
    
    if (!costEstimate) return null;

    const { estimated_cost, available_credits, sufficient_credits } = costEstimate;

    const formatCost = (cost) => {
        return typeof cost === 'number' ? cost.toFixed(2) : '0.00';
    };

    return (
        <Dialog open={true} onClose={onCancel} className="relative z-50">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
            
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-lg w-full bg-white rounded-2xl shadow-xl">
                    <div className="p-6">
                        <div className="text-center mb-6">
                            <div className="mx-auto flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 mb-4">
                                <CpuChipIcon className="w-5 h-5 text-gray-600" />
                            </div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
                                {t('ResearchPipeline.costEstimation.title')}
                            </Dialog.Title>
                        </div>

                        <div className="space-y-4">
                            {/* Cost Summary */}
                            <div className="text-center">
                                <p className="text-gray-600 text-sm">
                                    {t('ResearchPipeline.costEstimation.costSummary')} <span className="font-bold text-black">{formatCost(estimated_cost)}</span> {t('ResearchPipeline.costEstimation.creditsToRun')}
                                    {!sufficient_credits && t('ResearchPipeline.costEstimation.insufficientCredits')}
                                </p>
                            </div>

                            {/* Information Note */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-4 h-4 rounded-full bg-black flex items-center justify-center mt-0.5">
                                        <span className="text-white text-xs font-bold">i</span>
                                    </div>
                                    <div className="text-sm text-gray-700">
                                        <p>
                                            {t('ResearchPipeline.costEstimation.infoText')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center mt-6">
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            >
{t('ResearchPipeline.costEstimation.cancel')}
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={!sufficient_credits}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                                    sufficient_credits
                                        ? 'bg-black text-white hover:bg-gray-800'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {sufficient_credits ? (
                                    <>
{t('ResearchPipeline.costEstimation.createPipeline')}
                                        <div className="flex items-center gap-1 ml-2">
                                            <CpuChipIcon className="w-4 h-4" />
                                            {formatCost(estimated_cost)}
                                        </div>
                                    </>
                                ) : (
                                    t('Common.getMoreCredits')
                                )}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

export default CostEstimationModal;